import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";

import BasicModal from "@/Modals/BasicModal";
import RenderToast from "@/components/RenderToast";
import PlayerCareerInitialValues from "@/data/Profiles/PlayerCareers/initial-values";
import PlayerCareerSchema from "@/schema/WebDataManagement/Mapping/PlayerCareers/PlayerCareerSchema";
import StatusInput from "@/components/StatusInput";
import {
  playerCareersApi,
  termsMasterDataApi,
  teamsMasterDataApi,
  playersApi,
} from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { useSelector } from "react-redux";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";

const PlayerCareerModal = forwardRef((_, ref) => {
  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );

  const localeCodes = useMemo(() => {
    return locales ? locales.map((locale) => locale.code) : [];
  }, [locales]);

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id ? `${playerCareersApi}/${modalState.id}` : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const defaultTermLabelFormatter = useCallback((item) => item.name, []);

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Player Career ${action} successfully.`,
    });
  };
  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${playerCareersApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: playerCareersApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: PlayerCareerInitialValues(modalState.id ? showData : null),
    validationSchema: PlayerCareerSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;
  const teamListApi = `${teamsMasterDataApi}${baseQueryParams}`;
  const {
    loading: teamLoading,
    defaultOptions: teamDefaultOptions,
    debouncedFetchOptions: teamFetchOptions,
    getSelectedOption: teamGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: teamListApi,
    formikValue: formik.values.team_id,
    formikInitialValue: formik.initialValues.team_id,
    showData: !!modalState?.id && showData?.data?.team,
  });

  const termListApi = `${termsMasterDataApi}${baseQueryParams}`;
  const {
    loading: termLoading,
    defaultOptions: termDefaultOptions,
    debouncedFetchOptions: termFetchOptions,
    getSelectedOption: termGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: termListApi,
    formikValue: formik.values.term_id,
    formikInitialValue: formik.initialValues.term_id,
    showData: !!modalState?.id && showData?.data?.term,
    labelFormatter: defaultTermLabelFormatter,
  });

  const playerListApi = `${playersApi}${baseQueryParams}`;
  const {
    loading: playerLoading,
    defaultOptions: playerDefaultOptions,
    debouncedFetchOptions: playerFetchOptions,
    getSelectedOption: playerGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: playerListApi,
    formikValue: formik.values.player_id,
    formikInitialValue: formik.initialValues.player_id,
    showData: !!modalState?.id && showData?.data?.player,
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) formData.append("_method", "PUT");

    formData.append("status", values.status);
    formData.append("player_id", values.player_id);
    formData.append("team_id", values.team_id);
    formData.append("term_id", values.term_id);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Player Career" : "Add Player Career"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Col md={12}>
        <AsyncDropdown
          label="Player"
          placeholder="No Player selected. Type to search..."
          defaultOptions={playerDefaultOptions}
          loadOptions={playerFetchOptions}
          isLoading={playerLoading}
          value={playerGetSelectedOption()}
          onChange={(selected) => {
            formik.setFieldValue(
              "player_id",
              selected?.value || formik.values.player_id || null
            );
          }}
          errorText={
            (formik.touched.player_id && formik.errors.player_id) ||
            (saveError?.status === 422 &&
              saveError?.response?.data?.errors?.player_id?.join(" "))
          }
        />
      </Col>

      <Col md={12}>
        <AsyncDropdown
          label="Team"
          placeholder="No Team selected. Type to search..."
          defaultOptions={teamDefaultOptions}
          loadOptions={teamFetchOptions}
          isLoading={teamLoading}
          value={teamGetSelectedOption()}
          onChange={(selected) => {
            formik.setFieldValue(
              "team_id",
              selected?.value || formik.values.team_id || null
            );
          }}
          errorText={
            (formik.touched.team_id && formik.errors.team_id) ||
            (saveError?.status === 422 &&
              saveError?.response?.data?.errors?.term_id?.join(" "))
          }
        />
      </Col>

      <Col md={12}>
        <AsyncDropdown
          label="Term"
          placeholder="No Term selected. Type to search..."
          defaultOptions={termDefaultOptions}
          loadOptions={termFetchOptions}
          isLoading={termLoading}
          value={termGetSelectedOption()}
          onChange={(selected) => {
            formik.setFieldValue(
              "term_id",
              selected?.value || formik.values.term_id || null
            );
          }}
          errorText={
            (formik.touched.term_id && formik.errors.term_id) ||
            (saveError?.status === 422 &&
              saveError?.response?.data?.errors?.term_id?.join(" "))
          }
        />
      </Col>
    </BasicModal>
  );
});

export default PlayerCareerModal;
